import { FleetMapAreaDataTestIds } from '@backoffice-frontend/common'
import { trackClickEvent } from '@backoffice-frontend/tracking'
import { LoadingSkeleton } from '@moia-dev/pace-web'
import type { VehicleListOverlayVehicleFragment } from './VehicleListOverlay.hook'
import { VehicleListOverlayRow } from './VehicleListOverlayRow'

const { List } = FleetMapAreaDataTestIds.Vehicle

type VehicleListOverlayProps = {
  selectedVehicleId?: string
  vehicles: VehicleListOverlayVehicleFragment[]
  onVehicleSelect: (id: string) => void
}

const SKELETON_ROW_COUNT = 10

const overlayContainerStyles = (theme: {
  semantic: { ColorSurfaceDefault: string }
  radius: { RadiusMedium: string }
  shadows: string[]
}) => ({
  userSelect: 'none' as const,
  background: theme.semantic.ColorSurfaceDefault,
  borderRadius: theme.radius.RadiusMedium,
  boxShadow: theme.shadows[2],
})

export const VehicleListOverlaySkeleton = () => {
  return (
    <div css={theme => overlayContainerStyles(theme)}>
      {Array.from(
        { length: SKELETON_ROW_COUNT },
        (_, i) => `skeleton-${i}`,
      ).map(key => (
        <div
          key={key}
          css={theme => ({
            display: 'flex',
            padding: `${theme.space.Space2} ${theme.space.Space3}`,
            alignItems: 'center',
            borderBottom: `1px solid ${theme.semantic.ColorBorderOnSelectedDefault}`,
            '&:last-child': {
              borderBottom: 'none',
            },
          })}
        >
          <LoadingSkeleton css={{ width: 58, height: 18 }} />
        </div>
      ))}
    </div>
  )
}

export const VehicleListOverlay = ({
  selectedVehicleId,
  vehicles,
  onVehicleSelect,
}: VehicleListOverlayProps) => {
  return (
    <div
      css={theme => ({
        ...overlayContainerStyles(theme),
        overflowY: 'auto',
      })}
      data-testid={List}
    >
      {vehicles.map(vehicle => (
        <VehicleListOverlayRow
          key={vehicle.id}
          vehicle={vehicle}
          isSelected={selectedVehicleId === vehicle.id}
          onClick={() => {
            trackClickEvent(`fleetmap_list__click_vehicle`)
            onVehicleSelect(vehicle.id)
          }}
        />
      ))}
    </div>
  )
}
